import * as React from 'react';
import ReactDOM from 'react-dom';
 // @ts-ignore
import StateViewBar, {IStateProperty} from '../components/StateViewBar';

import ReactDataGrid from 'react-data-grid';
import TextField from './TextField';

/** Enum for all values, which defines the editable property of some value */
export enum EditableValue {
    Disabled = 0,
    Readonly = 1,
    Editable = 2,
    Unvisible = 3
}

/** filter */
export interface Filter {
    /** filter term */
    filterTerm: any;
    /** column to be filtered */
    column: IColumnDefinition;
}

/** properties of filter renderer */
export interface IFilterRendererProps {
    /** column to be filtered */
    column: IColumnDefinition;
    /** method to be called to raise filtering */
    onChange(filter: Filter): void;
}

/** type interface for ABaseColumn */
export interface BaseColumnClass<P extends IBaseColumnProps = IBaseColumnProps>
    extends React.ComponentClass<P> {
    /** A custom formatter for this column's filter. */
    filterRenderer?: React.ReactElement<IFilterRendererProps> | React.ComponentClass<IFilterRendererProps> | React.StatelessComponent<IFilterRendererProps>;
    /**
     * method to check data be valid on filter or not
     * @param filter filter
     * @param rowData row data to be checked for validness after filtering
     * @returns true if data is not filtered out, false otherwise
     */
    filterValid(filter: Filter, rowData: any): boolean;

    new(props: P, context?: any): ABaseColumn;
}

export type IStateProperty = IStateProperty;
export interface IValueState {
    /**
     * A unique key for this column value state.
     * Each row should have a property with this name, which contains this column's value state.
     */
    key: string;
    /** Array of state button properties, or a unique name of a property, which returns IStateProperty[] for the cell.  */
    states: string | IStateProperty[];

    /**
     * A unique key for this column value state editable.
     * Each row should have a property with this name, which contains a value of the type EditableValue.
     */
    keyEditable?: string | EditableValue;

    /**
     * this event will be called on changing of the values.
     *
     * @param cellKey   the property which is changed
     * @param value the object with the changed property
     */
    onChange?(cellKey: string, value: object): void;
}

/** column definition */
export interface IColumnDefinition extends ReactDataGrid.Column {
    /** origin index by the initializing of columns */
    originIndex?: number;
    /**
     * A property that's unique to every row.
     * @default 'id'
     */
    rowKey?: string;

    /** Get a reference to the underlying data grid that the column belongs to. */
    getGrid?: () => ReactDataGrid | null;

    /**
     * If defined, the value state bar will be shown. See the property 'valueStateHeight' to make it visible!
     */
    valueState?: IValueState;

    /**
     * The height of the value state view bar.
     * If 0, the valueState will not be shown and the space is not allocated.
     * @default 0
     */
    valueStateHeight?: number;

    /**
     * A unique key for this column value editable.
     * Each row should have a property with this name, which contains a value of the type EditableValue.
     */
    keyEditable?: string | EditableValue;

    /**
     * placeholder for filter
     */
    filterPlaceholder?: string;

    /**
     * method to check data be valid on filter or not
     * @param filter filter
     * @param rowData row data to be checked for validness after filtering
     * @returns true if data is not filtered out, false otherwise
     */
    filterValid?(filter: Filter, rowData: any): boolean;

    /**
     * for text filter, defines the behavior:
     * if false, the '*' can be used to replace unknown parts in the search string.
     * if true, the '*' will not be used to replace missing parts, you have to write the signs exactly.
     * @default false
     */
    exactFiltering?: boolean;

    /** defines whether the rows should be grouped, when sorted by this column */
    groupable?: boolean;

    /** additional properties */
    [key: string]: any;
}

/** this properties will be set by BasicDataGrid */
export interface IBaseColumnProps {
    /** value, which should be displayed or edited */
    value: any;
    /** information about the column, filled only in edit mode */
    column?: IColumnDefinition;
    /** whole data of the row, one of which values is to be edited. Filled only in edit mode */
    rowData?: any;
    /** this information is defined in not edit mode */
    dependentValues?: {rowdata: any, column: IColumnDefinition};
}

export interface IBaseColumnState {
    column: IColumnDefinition;
    rowData: any;
    value: any;
}

/**
 * this class is used as base class for column rendering in BasicDataGrid
 * @author akirillov
 */
export default abstract class ABaseColumn<P extends IBaseColumnProps = IBaseColumnProps, S extends IBaseColumnState = IBaseColumnState>
    extends React.Component<P, S>
{
    id: string;
    disableContainerStyles: boolean; // disable to avoid setting height of sub elements by grid

    /** node to be focused on start editing */
    private domNode: Element;

    /** activates edit mode - in this mode the onChange events will be generated */
    private editMode: boolean;

    private backgroundDisabled: string = 'lightgrey';

    static getEditModeFromKeyEditable(keyEditable: string | EditableValue, rowData: any): EditableValue {
        return typeof keyEditable === 'string' ? rowData[keyEditable] : keyEditable;
    }

    static getStatesFromKey(keyStates: string | IStateProperty[], rowData: any): IStateProperty[] | undefined {
        return typeof keyStates === 'string' ? rowData[keyStates] : keyStates;
    }

    /**
     * A column specific formatter for this column's filter. Overwrite it to have other renderer.
     */
    static get filterRenderer():
        React.ReactElement<IFilterRendererProps> |
        React.ComponentClass<IFilterRendererProps> |
        React.StatelessComponent<IFilterRendererProps> |
        undefined {
        return (props: IFilterRendererProps) => {
            const key: string = 'filterRenderer_' + props.column.key + '_' + props.column.originIndex;
            let thisInput:TextField;

            return (
                <TextField
                    key={key}
                    id={key}
                    ref={(input)=>thisInput=input!}
                    placeholder={props.column.filterPlaceholder}
                    underlineShow={true}
                    onChange={(event: React.FormEvent<{}>, newValue: string) => {
                            props.onChange({column: props.column, filterTerm: newValue});
                    }}
                />
            );
        };
    }

    /**
     * a column specific method to check data be valid on filter or not. Overwrite it to have other check.
     *
     * Basic implementation looks for the exact words (case insensitive) with following special signs:
     * with + you can look for more than one word, ex. "Porsche+Auto" shows lines where Porsche and Auto are found.
     * with - you can exclude words, ex. "Jaguar -Auto" shows only lines with Jaguar but without Auto.
     * with | you can look for either one or another word, ex. "Porsche|Auto" shows lines where either Porsche or Auto (or both) are found.
     * with * you can replace the parts you don't know if the Filter.column.exactFiltering is set to false.
     *
     * @param filter filter
     * @param rowData row data to be checked for validness after filtering
     * @returns true if data is not filtered out, false otherwise
     */
    static filterValid(filter: Filter, rowData: any): boolean {
        const rowValue = rowData[filter.column.key];
        if (rowValue === undefined)
            return false;

        const value: string = rowValue.toString().toLowerCase();
        const filterGroups: string[] = filter.filterTerm.toString().trim().toLowerCase().split("|");
        return ABaseColumn.isValidFilterValue(filterGroups, value, filter.column.exactFiltering || false);
    }

    /**
     * Figure out whether a string is part of a given filter groups.
     *
     * Basic implementation looks for the exact words (case insensitive) with following special signs:
     * with + you can look for more than one word, ex. "Porsche+Auto" shows lines where Porsche and Auto are found.
     * with - you can exclude words, ex. "Jaguar -Auto" shows only lines with Jaguar but without Auto.
     * with | you can look for either one or another word, ex. "Porsche|Auto" shows lines where either Porsche or Auto (or both) are found.
     * with * you can replace the parts you don't know if the Filter.column.exactFiltering is set to false.
     *
     * @param filterGroups filter groups
     * @param value the string to be checked for validness after filtering (usually a column's row data)
     * @param exactFiltering whether to use exact filtering or not
     * @returns true if data is not filtered out, false otherwise
     */
    protected static isValidFilterValue(filterGroups: string[], value: string, exactFiltering: boolean): boolean {
        if (!value)
            return false;

        let result = true;
        for (let i = 0; i < filterGroups.length; ++i) {
            const filterGroup = filterGroups[i];
            let cw = '';
            let prevSpace = true;
            let isAdd = true;
            let andGroups: Array<string> = []; // all should match
            let excGroups: Array<string> = []; // exclude if one matchs
            let grps;
            for (let j = 0; j < filterGroup.length; ++j) {
                const c = filterGroup[j];
                grps = undefined;
                if (c === '+') {
                    grps = isAdd ? andGroups : excGroups;
                    isAdd = true;
                    prevSpace = true;
                }
                else if (c === '-' && prevSpace) {
                    grps = isAdd ? andGroups : excGroups;
                    isAdd = false;
                    prevSpace = true;
                }

                if (grps) {
                    if (cw.length > 0)
                        grps.push(cw.trim());
                    cw = '';
                }
                else {
                    cw += c;
                }

                prevSpace = c === ' ';
            }
            grps = isAdd ? andGroups : excGroups;
            if (cw.length > 0)
                grps.push(cw.trim());

            // check if there are some exclusive criteria
            let exclResult = false;
            excGroups.forEach((m: string) => {exclResult = exclResult || ABaseColumn.isMatch(value, m, exactFiltering);});
            if (exclResult) {
                result = false;
                continue;
            }

            // check if all and-criteria fulfills
            let andResult = true;
            andGroups.forEach((m: string) => {andResult = andResult && ABaseColumn.isMatch(value, m, exactFiltering);});
            result = andResult;

            // if at least one group fulfills - ready
            if (result)
                break;
        }

        return result;
    }

    /**
     * checks the matching of value to match. Handles the *.
     * @param value value to be matched
     * @param match match for the value
     * @param exactFiltering if false, the '*' can be used to replace unknown parts in the search string.
     *                       if true, the '*' will not be used to replace missing parts, you have to write the signs exactly.
     */
    protected static isMatch(value: string, match: string, exactFiltering: boolean): boolean {
        let result = true;
        if (exactFiltering) {
            result = (0 <= value.indexOf(match));
        }
        else {
            const matches: Array<string> = match.split('*');
            let n = 0;
            for (let i = 0; i < matches.length; ++i) {
                n = value.indexOf(matches[i], n);
                if (n < 0) {
                    result = false;
                    break;
                }
            }
        }
        return result;
    }

    private static getColumnAndRowData(props: Readonly<IBaseColumnProps>): {rowdata: any, column: IColumnDefinition} {
        if (props.column && props.rowData) {
            return {rowdata: props.rowData, column: props.column};
        }
        else if (props.dependentValues) {
            return props.dependentValues;
        }
        else {
            throw new Error("non-supported scenario: column and rowdata not defined!");
        }
    }

    constructor(props: P) {
        super(props);
        this.editMode = props.rowData ? true : false; // props.rowData is set only in edit mode
        this.disableContainerStyles = true;
        // !!!don't set state in constructor!!!

        this.onChange = this.onChange.bind(this);
        this.setInputNode = this.setInputNode.bind(this);
        this.renderCustom = this.renderCustom.bind(this);
        this.getEditMode = this.getEditMode.bind(this);
        this.getCell = this.getCell.bind(this);
        this.getId = this.getId.bind(this);
        this.getEditModeForValueState = this.getEditModeForValueState.bind(this);
    }

    componentWillMount() {
        // !!!don't set state in constructor!!!
        const {rowdata, column} = ABaseColumn.getColumnAndRowData(this.props);
        const {rowKey = 'id'} = column;
        this.id = rowKey in rowdata ? ("" + rowdata[rowKey]) : "Column" + Math.random();
        this.setState(
            {
                column: column,
                rowData: rowdata,
                value: this.props.value,
            }
        );
    }

    /** generates id for rendered element */
    protected getId(random: string = "") {
        return this.id + random;
    }

    /** returns the grid cell containing this element */
    getCell(): HTMLElement | undefined {
        let cell = undefined;
        let element: HTMLElement | null = ReactDOM.findDOMNode(this) as HTMLElement;
        while (element) {
            if (element.className === 'react-grid-Cell__value') {
                cell = element;
                break;
            }
            element = element.parentElement;
        }
        return cell;
    }

    componentWillReceiveProps(nextProps: Readonly<IBaseColumnProps>): void {
        let newState: IBaseColumnState = Object.assign({}, this.state);

        if (nextProps.value !== this.props.value) {
            newState.value = nextProps.value;
        }
        if (nextProps.dependentValues !== this.props.dependentValues
            || nextProps.column !== this.props.column
            || nextProps.rowData !== this.props.rowData
        ) {
            const {rowdata, column} = ABaseColumn.getColumnAndRowData(nextProps);
            newState.column = column;
            newState.rowData = rowdata;
        }
        this.setState(newState);
    }

    getStyle(): {width: string} {
        return {
            width: '100%'
        };
    }

    getValue(): any {
        let updated = {};
        updated[this.state.column.key] = this.state.value;
        return updated;
    }

    /**
     * returns the DOM node to receive the focus on switch in edit mode.
     * use setInputNode to set it ex. in renderCustom.
     */
    getInputNode(): Element {
        return this.domNode;
    }

    /**
     * sets the DOM node to receive the focus on switch in edit mode.
     * @param domNode node to receive the focus
     */
    setInputNode(domNode: Element | any) {
        this.domNode = domNode;
    }

    inheritContainerStyles(): boolean {
        return true;
    }

    /**
     * sets the newValue into state.value
     * @param newValue new value
     * @param callback optional callback after the new value is set
     */
    onChange(newValue: any, callback?: () => void): void {
        this.setState({value: newValue}, callback);
    }

    /**
     * handles the request of changing the value state
     * @param stateId new value state requested
     * @param column current column
     * @param rowData current row data before changes
     */
    handleChangeValueState(stateId: number, column: IColumnDefinition, rowData: any) {
        const editMode: EditableValue = this.getEditModeForValueState();
        if (column.valueState && column.valueState.onChange && editMode === EditableValue.Editable) {
            let newData = {};
            const {rowKey = 'id'} = column;
            newData[rowKey] = rowData[rowKey];
            newData[column.valueState.key] = stateId;
            column.valueState.onChange(column.valueState.key, newData);
        }
    }

    /**
     * custom rendering of value part
     * @param editMode rendering in edit mode or not
     */
    abstract renderCustom(editMode: boolean): JSX.Element;

    getEditModeForValueState(): EditableValue {
        let editMode: EditableValue | undefined = undefined;
        if (this.state.column.valueState) {
            const {keyEditable} = this.state.column.valueState;
            if (keyEditable !== undefined) {
                editMode = ABaseColumn.getEditModeFromKeyEditable(keyEditable, this.state.rowData);
            }
        }

        if (editMode === undefined) {
            // make similar to the cell but not editable
            editMode = this.getEditMode();
            if (editMode === EditableValue.Editable) {
                editMode = EditableValue.Readonly;
            }
        }
        return editMode;
    }

    renderValueState(): JSX.Element | null {
        const height = this.state.column.valueStateHeight !== undefined ? this.state.column.valueStateHeight : 0;
        if (0 === height || this.state.column.valueState === undefined)
            return null;

        if (this.state.column.valueState) {
            const {key, states} = this.state.column.valueState;
            const editMode: EditableValue = this.getEditModeForValueState();
            let style: {background?: string} = {};
            if (editMode === EditableValue.Disabled) {
                style.background = this.backgroundDisabled;
            }

            const valueStates = ABaseColumn.getStatesFromKey(states, this.state.rowData);

            if (editMode !== EditableValue.Unvisible && valueStates !== undefined && valueStates.length > 0) {
                return (
                    <div style={style}>
                        <StateViewBar
                            height={height}
                            states={valueStates}
                            active={this.state.rowData[key]}
                            changeTypeSequential={false}
                            editMode={editMode === EditableValue.Editable}
                            onChange={(stateId: number) => this.handleChangeValueState(stateId, this.state.column, this.state.rowData)}
                        />
                    </div>
                );
            }
            else {
                // fall through!!!
            }
        }

        // show placeholder if valueState editMode === EditableValue.Unvisible
        {
            const editMode = this.getEditMode();
            // should be similar to StateViewBar
            const vp = "0 0 50 50";
            let style: {background?: string} = {};
            if (editMode === EditableValue.Disabled) {
                style.background = this.backgroundDisabled;
            }
            return (
                <div style={style}>
                    <div style={{width: height}}>
                        <svg viewBox={vp} />
                    </div>
                </div>
            );
        }
    }

    getEditMode(): EditableValue {
        let editMode: EditableValue = EditableValue.Readonly;
        if (this.state.column.keyEditable !== undefined) {
            editMode = ABaseColumn.getEditModeFromKeyEditable(this.state.column.keyEditable, this.state.rowData);
        }
        if (!this.editMode && EditableValue.Editable === editMode)
            editMode = EditableValue.Readonly;
        return editMode;
    }

    render() {
        const editMode = this.getEditMode();
        let style: {background?: string, height?: any} = {height: '100%'};
        if (editMode === EditableValue.Disabled) {
            style.background = this.backgroundDisabled;
        }
        if (editMode === EditableValue.Unvisible) {
            return <div />;
        }
        else {
            return (
                <div className='cell-container'>
                    {this.renderValueState()}
                    <div style={style} className="cell-container">
                        {this.renderCustom(editMode === EditableValue.Editable)}
                    </div>
                </div>
            );
        }
    }
}

/**
 * Base column implementation, usable for string values.
 */
export class BaseColumn extends ABaseColumn {
    /**
     * custom rendering of value part
     * @param editMode rendering in edit mode or not
     */
    renderCustom(editMode: boolean) {
        if (editMode) {
            return (
                <TextField
                    id={this.getId()}
                    readonly={!editMode}
                    underlineShow={editMode}
                    ref={ref => {
                        if (ref) {
                            const domNode = ref.getInputNode();
                            this.setInputNode(domNode ? domNode : ReactDOM.findDOMNode(this));
                        }
                    }}
                    value={(this.state.value !== undefined && this.state.value !== null) ? this.state.value.toString() : JSON.stringify(this.state.value)}
                    onChange={(event: React.FormEvent<{}>, newValue: string) => this.onChange(newValue)}
                />
            );
        } else {
            return (
                <div>{(this.state.value !== undefined && this.state.value !== null) ? this.state.value.toString() : JSON.stringify(this.state.value)}</div>
            );
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/BaseColumn.tsx